import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, MapPin, Home, DollarSign, Calendar, Zap, ExternalLink } from "lucide-react";
import { currency } from "@/lib/constants";

/**
 * On-demand ATTOM property lookup for authenticated users.
 * Every successful lookup consumes 1 ATTOM API call (or a cache hit).
 */
export default function AttomLookupPage() {
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!address1.trim() || !address2.trim()) {
      toast.error("Enter street address + city, state ZIP");
      return;
    }
    setLoading(true);
    setResult(null);
    setNotFound(false);
    try {
      const r = await api.get("/attom/property/lookup", {
        params: { address1: address1.trim(), address2: address2.trim() },
      });
      if (r.data.found) {
        setResult(r.data);
      } else {
        setNotFound(true);
        toast.error("Property not found in ATTOM database");
      }
    } catch (e) {
      if (e.response?.status === 429) {
        toast.error("Monthly ATTOM quota exhausted — try again next month");
      } else {
        toast.error(e.response?.data?.detail || "Lookup failed");
      }
    } finally { setLoading(false); }
  };

  const p = result?.property;
  const raw = result?.raw;

  return (
    <div className="space-y-6" data-testid="attom-lookup-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold flex items-center gap-2">
          <Zap size={12} /> ATTOM Live Property Lookup
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Property Lookup.</h1>
        <p className="text-[13px] text-[#b8ac96] mt-2 max-w-2xl">
          Pull a live property card from ATTOM's 160M+ property database. Get valuation, ownership, tax, and building details for any US address.
        </p>
      </div>

      {/* Search form */}
      <form onSubmit={submit} className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5 space-y-3" data-testid="attom-search-form">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1">Street Address</label>
            <input
              value={address1}
              onChange={(e) => setAddress1(e.target.value)}
              placeholder="468 SEQUOIA DR"
              data-testid="attom-address1"
              className="w-full bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-[#d4a574]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#7a6d5a] block mb-1">City, State ZIP</label>
            <input
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
              placeholder="SMYRNA, DE 19977"
              data-testid="attom-address2"
              className="w-full bg-[#241d16] border border-[#33291f] rounded px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-[#d4a574]"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="attom-search-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[13px] hover:bg-[#e8b988] disabled:opacity-60"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {loading ? "Fetching from ATTOM…" : "Look Up Property"}
        </button>
        <div className="text-[10px] font-mono text-[#7a6d5a]">
          Powered by ATTOM Data · Cached 24h to conserve quota
        </div>
      </form>

      {/* Results */}
      {notFound && !result && (
        <div className="border border-[#f87171]/30 bg-[#f87171]/5 rounded-lg p-6 text-center" data-testid="attom-not-found">
          <div className="text-[#f87171] font-display text-lg mb-1">No match found</div>
          <div className="text-[13px] text-[#b8ac96]">Try formatting the address differently or verify the ZIP code. Example: `468 SEQUOIA DR` + `SMYRNA, DE 19977`</div>
        </div>
      )}

      {p && (
        <div className="grid lg:grid-cols-3 gap-4" data-testid="attom-result">
          {/* Left column — hero card */}
          <div className="lg:col-span-2 border border-[#33291f] rounded-lg bg-[#1c1712] overflow-hidden">
            <div className="aspect-[16/8] relative">
              <img src={p.image_url} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#14100c] via-transparent to-transparent" />
              <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded bg-[#14100c]/90 text-[10px] uppercase tracking-wider font-semibold text-[#d4a574]">
                <Zap size={11} /> ATTOM Live
              </div>
              <div className="absolute bottom-3 left-4">
                <div className="text-[10px] uppercase tracking-wider text-[#f4ede2]/70 mb-1">{p.property_type}</div>
                <div className="font-display text-2xl font-bold text-[#f4ede2]">{p.address}</div>
                <div className="text-[13px] text-[#f4ede2]/80 mt-0.5">
                  <MapPin size={11} className="inline mr-1" /> {p.city}, {p.state} {p.zip}
                </div>
              </div>
            </div>
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniStat label="Estimated Value" value={p.estimated_value ? currency(p.estimated_value) : "—"} accent="#d4a574" />
              <MiniStat label="Building Size" value={p.building_size_sqft ? `${p.building_size_sqft.toLocaleString()} sqft` : "—"} />
              <MiniStat label="Lot Size" value={p.lot_size_sqft ? `${p.lot_size_sqft.toLocaleString()} sqft` : "—"} />
              <MiniStat label="Year Built" value={p.year_built || "—"} />
              <MiniStat label="Tax Assessment" value={p.tax_assessment ? currency(p.tax_assessment) : "—"} />
              <MiniStat label="Annual Tax" value={p.tax_amount_due ? currency(p.tax_amount_due) : "—"} />
              <MiniStat label="Last Sale" value={p.last_sale_amount ? currency(p.last_sale_amount) : "—"} />
              <MiniStat label="County" value={p.county || "—"} />
            </div>
          </div>

          {/* Right column — actions + raw JSON */}
          <div className="space-y-4">
            <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5">
              <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a] mb-3">Quick Actions</div>
              <a
                href={`https://www.google.com/maps?q=${encodeURIComponent(p.address + " " + p.city + " " + p.state)}`}
                target="_blank" rel="noreferrer"
                data-testid="attom-view-map"
                className="flex items-center gap-2 px-3 py-2 rounded border border-[#4a3a2b] text-[12px] text-[#d4a574] hover:border-[#d4a574] mb-2"
              >
                <MapPin size={13} /> View on Google Maps <ExternalLink size={11} className="ml-auto" />
              </a>
              <a
                href={`https://www.zillow.com/homes/${encodeURIComponent(p.address + " " + p.city + " " + p.state + " " + p.zip)}`}
                target="_blank" rel="noreferrer"
                data-testid="attom-view-zillow"
                className="flex items-center gap-2 px-3 py-2 rounded border border-[#4a3a2b] text-[12px] text-[#d4a574] hover:border-[#d4a574]"
              >
                <Home size={13} /> Cross-check on Zillow <ExternalLink size={11} className="ml-auto" />
              </a>
            </div>

            <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5">
              <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a] mb-2">ATTOM ID</div>
              <div className="font-mono text-[12px] text-[#d4a574] break-all">{p.raw_attom_id}</div>
              <details className="mt-4">
                <summary className="cursor-pointer text-[11px] text-[#7a6d5a] hover:text-[#d4a574]">View raw ATTOM response</summary>
                <pre className="text-[9px] font-mono text-[#7a6d5a] bg-[#14100c] rounded mt-2 p-2 max-h-60 overflow-auto">
                  {JSON.stringify(raw, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      )}

      {!result && !notFound && !loading && (
        <div className="border border-dashed border-[#33291f] rounded-lg p-8 text-center bg-[#1c1712]/40" data-testid="attom-empty-state">
          <div className="text-[13px] text-[#b8ac96] mb-2">Try these example addresses:</div>
          <div className="flex justify-center flex-wrap gap-2 mt-3">
            {[
              { a1: "468 SEQUOIA DR", a2: "SMYRNA, DE 19977" },
              { a1: "586 FRANKLIN AVE", a2: "BROOKLYN, NY 11238" },
              { a1: "210 HAMMERKING RD", a2: "DALLAS, TX 75232" },
            ].map((ex, i) => (
              <button
                key={i}
                onClick={() => { setAddress1(ex.a1); setAddress2(ex.a2); }}
                data-testid={`attom-example-${i}`}
                className="text-[11px] font-mono px-3 py-1.5 rounded border border-[#33291f] text-[#d4a574] hover:border-[#d4a574]"
              >
                {ex.a1}, {ex.a2}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a] mb-1">{label}</div>
      <div className="font-display text-[16px] font-semibold" style={{ color: accent || "#f4ede2" }}>{value}</div>
    </div>
  );
}
